/**
 * The claim code callout.
 *
 * The code is returned by exactly one response and stored only as a hash, so this
 * is the single moment it exists anywhere an operator can see it. Losing it means
 * reissuing, not recovering.
 *
 * It therefore stays on screen until dismissed deliberately — no timeout, no
 * disappearing toast — and says plainly that it will not be shown again.
 */

import { useState } from 'react';

import { claimCodeStatus } from './forms.ts';

interface Props {
  code: string;
  expiresAt: string;
  deviceName: string;
  now: number;
  onDismiss: () => void;
}

export function ClaimCode({ code, expiresAt, deviceName, now, onDismiss }: Props): React.JSX.Element {
  const [copied, setCopied] = useState(false);
  const status = claimCodeStatus(expiresAt, now);

  async function copy(): Promise<void> {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard access is refused on insecure origins and in some browsers. The
      // code is displayed regardless, so this is a convenience failing rather
      // than the feature failing.
    }
  }

  return (
    <div className="claim" role="status">
      <p className="claim__label">Claim code</p>
      <p className="claim__device">for {deviceName}</p>

      {/* Large and monospaced: this gets read aloud down a phone line. */}
      <p className="claim__code">{code}</p>

      <p className={status.expired ? 'claim__expiry claim__expiry--expired' : 'claim__expiry'}>
        {status.label}
      </p>

      <p className="claim__warning">
        Shown once. It is stored only as a hash and cannot be retrieved — if it is
        lost, generate a new one from the device below.
      </p>

      <div className="claim__actions">
        <button type="button" className="btn btn--primary" onClick={() => void copy()}>
          {copied ? 'Copied' : 'Copy code'}
        </button>
        <button type="button" className="btn" onClick={onDismiss}>
          Done
        </button>
      </div>
    </div>
  );
}
